/**
 * @fileoverview Standardized Tool UI Components
 * 
 * This module provides a common set of UI components that all tools must use
 * to ensure consistent styling and behavior across the application.
 * 
 * All tools should use these components instead of implementing their own UI.
 */

import React from 'react';
import { Button } from '../../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Input } from '../../../components/ui/input';
import { LucideIcon } from 'lucide-react';

// Common tool component interfaces
export interface ToolButtonProps {
  icon?: LucideIcon;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  variant?: 'default' | 'outline' | 'secondary' | 'ghost';
  size?: 'sm' | 'default' | 'lg';
  className?: string;
}

export interface ToolSelectProps {
  icon?: LucideIcon;
  placeholder: string;
  value?: string;
  onValueChange: (value: string) => void;
  disabled?: boolean;
  options: Array<{
    value: string;
    label: string;
    icon?: LucideIcon;
    description?: string;
  }>;
  className?: string;
}

export interface ToolInputProps {
  label?: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  type?: 'text' | 'number' | 'color';
  className?: string;
}

/**
 * Standardized Tool Button Component
 * All tool buttons must use this component for consistency
 */
export const ToolButton = React.createElement as any as (props: ToolButtonProps) => React.ReactElement;

// Implementation using React.createElement for .ts files
export const createToolButton = (props: ToolButtonProps) => {
  const { icon: Icon, label, onClick, disabled = false, variant = 'outline', size = 'sm', className = '' } = props;
  
  return React.createElement(Button, {
    onClick,
    disabled,
    variant,
    size,
    className: `${className}`.trim(),
    title: label
  }, [
    Icon && React.createElement(Icon, { 
      key: 'icon',
      className: 'w-4 h-4 mr-2' 
    }),
    React.createElement('span', { key: 'label' }, label)
  ].filter(Boolean));
};

/**
 * Standardized Tool Select Component
 * All tool dropdowns must use this component for consistency
 */
export const createToolSelect = (props: ToolSelectProps) => {
  const { icon: Icon, placeholder, value, onValueChange, disabled = false, options, className = '' } = props;
  
  return React.createElement(Select, {
    value,
    onValueChange,
    disabled
  }, [
    React.createElement(SelectTrigger, {
      key: 'trigger',
      className: `w-fit min-w-[120px] ${className}`.trim(),
      size: 'sm'
    }, [
      Icon && React.createElement(Icon, { 
        key: 'icon',
        className: 'w-4 h-4 mr-2' 
      }),
      React.createElement(SelectValue, { 
        key: 'value',
        placeholder 
      })
    ].filter(Boolean)),
    
    React.createElement(SelectContent, {
      key: 'content'
    }, options.map(option => 
      React.createElement(SelectItem, {
        key: option.value,
        value: option.value
      }, option.description ? 
        React.createElement('div', { className: 'flex flex-col' }, [
          React.createElement('div', { 
            key: 'label',
            className: 'flex items-center gap-2' 
          }, [
            option.icon && React.createElement(option.icon, { 
              key: 'icon',
              className: 'w-4 h-4' 
            }),
            React.createElement('span', { key: 'text' }, option.label)
          ].filter(Boolean)),
          React.createElement('div', { 
            key: 'desc',
            className: 'text-xs text-muted-foreground' 
          }, option.description)
        ]) : 
        React.createElement('div', { className: 'flex items-center gap-2' }, [
          option.icon && React.createElement(option.icon, { 
            key: 'icon',
            className: 'w-4 h-4' 
          }),
          React.createElement('span', { key: 'text' }, option.label)
        ].filter(Boolean))
      )
    ))
  ]);
};

/**
 * Standardized Tool Input Component
 * All tool inputs must use this component for consistency
 */
export const createToolInput = (props: ToolInputProps) => {
  const { label, placeholder, value, onChange, disabled = false, type = 'text', className = '' } = props;
  
  const input = React.createElement(Input, {
    type,
    placeholder,
    value,
    onChange: (e: any) => onChange(e.target.value),
    disabled,
    className: `${className}`.trim()
  });
  
  if (label) {
    return React.createElement('div', { className: 'flex flex-col gap-1' }, [
      React.createElement('label', { 
        key: 'label',
        className: 'text-xs font-medium text-muted-foreground' 
      }, label),
      React.createElement('div', { key: 'input' }, input)
    ]);
  }
  
  return input;
};

/**
 * Tool Component Factory
 * Provides a consistent way to create tool UI components
 */
export class ToolComponentFactory {
  /**
   * Create a standardized button for tools
   */
  static createButton(props: ToolButtonProps) {
    return createToolButton(props);
  }
  
  /**
   * Create a standardized select dropdown for tools
   */
  static createSelect(props: ToolSelectProps) {
    return createToolSelect(props);
  }
  
  /**
   * Create a standardized input for tools
   */
  static createInput(props: ToolInputProps) {
    return createToolInput(props);
  }
  
  /**
   * Create a tool container with consistent spacing and layout
   */
  static createContainer(children: React.ReactNode[], className = '') {
    return React.createElement('div', {
      className: `flex items-center gap-2 ${className}`.trim()
    }, children);
  }
  
  /**
   * Create a tool group with separator
   */
  static createGroup(children: React.ReactNode[], className = '') {
    return React.createElement('div', {
      className: `flex items-center gap-2 border-l border-border pl-2 ${className}`.trim()
    }, children);
  }
}

// Export common styling constants
export const TOOL_STYLES = {
  button: {
    variant: 'outline' as const,
    size: 'sm' as const
  },
  select: {
    size: 'sm' as const,
    minWidth: '120px'
  },
  input: {
    size: 'sm' as const
  },
  spacing: {
    gap: 'gap-2',
    padding: 'p-2'
  }
} as const;
